import { useEffect, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { getTenantId } from '@/services/tokenStorage'
import { registerEchoListener } from '@/services/echoClient'
import { apiSlice } from '@/redux/api/apiSlice'
import { setApprovalPrompt } from '@/redux/slices/aiSlice'
import { useGetAiPendingApprovalsQuery } from '@/redux/api/aiApi'

interface PendingApprovalPayload {
  conversation_id?: string | null
  approvals?: Array<{
    id: number
    conversation_id: string | null
    tool: string
    arguments: Record<string, unknown> | null
    reason: string | null
  }>
}

/**
 * Watches for pending AI tool approvals: in real time via the tenant's
 * private Echo channel when Reverb is connected, with an initial fetch
 * as the polling fallback (realtime only reaches terminals connected at
 * fire time — a drawer opened afterwards must still discover pending
 * approvals).
 *
 * The Echo effect depends ONLY on `enabled`: it used to re-run on every
 * refetch of the pending query (pendingApprovals in the deps), tearing the
 * channel subscription down and re-authorizing it (/broadcasting/auth) each
 * time. The REST fallback instead reacts to the query result in its own
 * effect, so fresh data never re-subscribes the channel.
 * Listeners go through the echoClient registry, so an Echo rebuild replays
 * them onto the new instance automatically.
 */
export function useAiApprovals(enabled: boolean) {
  const dispatch = useDispatch()
  // The endpoint returns { data: [...pending] }; refetchOnMountOrArgChange
  // makes this an initial-fetch fallback that re-arms whenever the hook
  // enables (drawer opened) — realtime pings cover the in-between moments.
  const { data: pendingApprovals } = useGetAiPendingApprovalsQuery(undefined, {
    skip: !enabled,
    refetchOnMountOrArgChange: true,
  })

  // Initial-fetch fallback — the REST endpoint returns { data: [...] } while
  // the broadcast payload is { conversation_id, approvals }. It must react to
  // the query resolving: a one-shot read inside the Echo effect was a no-op on
  // first enable, because the query has not resolved by then. Applied once
  // per enable so refetched data never re-opens a prompt the user dismissed.
  const initialFallbackAppliedRef = useRef(false)
  useEffect(() => {
    if (!enabled) {
      initialFallbackAppliedRef.current = false
      return
    }
    if (initialFallbackAppliedRef.current || !pendingApprovals) return
    initialFallbackAppliedRef.current = true

    const approvals =
      ((pendingApprovals as { data?: PendingApprovalPayload['approvals'] })?.data ??
        pendingApprovals) as PendingApprovalPayload['approvals']
    const first = approvals?.[0]
    if (!first) return
    dispatch(
      setApprovalPrompt({
        id: first.id,
        conversationId: first.conversation_id ?? null,
        tool: first.tool,
        arguments: first.arguments ?? null,
        reason: first.reason ?? null,
      }),
    )
  }, [enabled, pendingApprovals, dispatch])

  useEffect(() => {
    if (!enabled) return

    const applyPayload = (payload: PendingApprovalPayload) => {
      const first = payload?.approvals?.[0]
      if (!first) return
      dispatch(
        setApprovalPrompt({
          id: first.id,
          conversationId: first.conversation_id ?? payload.conversation_id ?? null,
          tool: first.tool,
          arguments: first.arguments ?? null,
          reason: first.reason ?? null,
        }),
      )
    }

    const tenantId = getTenantId()
    const channelName = tenantId ? `ai.tenant.${tenantId}` : null
    const unregisters: Array<() => void> = []

    if (channelName) {
      unregisters.push(
        registerEchoListener(channelName, '.ai.tool-approval-requested', (payload: PendingApprovalPayload) => {
          applyPayload(payload)
        }),
      )
      // A background analysis finished — the AI usage counters changed.
      // (This handler used to be an empty stub.)
      unregisters.push(
        registerEchoListener(channelName, '.ai.background-analysis-completed', () => {
          dispatch(apiSlice.util.invalidateTags(['AiUsage']))
        }),
      )
    } else if (import.meta.env.DEV) {
      // No tenant in storage => no channel => approvals only ever arrive via the
      // REST fallback. Surface it instead of failing silently.
      console.warn('[useAiApprovals] No tenant id in storage: realtime approval listener not registered.')
    }

    return () => {
      unregisters.forEach((unregister) => unregister())
    }
  }, [enabled, dispatch])
}
